/// <amd-module name="VSS/Platform/Components/ErrorBoundary/ErrorBoundary" />
import * as React from "react";
import { IWrappedComponentProperties, VssComponent, WrappedComponent } from "../../Layout";
import { IVssNavigationService } from "../../Navigation";
import { IVssTraceService, trace } from "../../Trace";
import { IErrorBoundaryProps } from "./ErrorBoundary.Props";

export interface ErrorBoundaryState {
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

/**
 * This is an ErrorBoundary that always shows child content, regardless of error state.
 * When there is an error state, the ErrorBoundary will render a WrappedComponent, of the given
 * type and props, as a sibling of the rest of the content.
 */
export class ErrorBoundary extends VssComponent<IErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: IErrorBoundaryProps) {
        super(props);
        this.state = {};
    }

    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({ error, errorInfo });

        if (error) {
            const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
            const routeId = navigationService.getCurrentRoute().routeId;

            const properties: { [key: string]: any } = {};
            if (errorInfo && errorInfo.componentStack) {
                properties["componentStack"] = errorInfo.componentStack;
            }
            properties["errorType"] = "ReactError";

            const traceService = this.context.pageContext.getService<IVssTraceService>("IVssTraceService");
            traceService.traceException("WebUnhandledExceptions", routeId, error, properties);
        }
    }

    public render() {
        if (this.state.error) {
            const additionalHandlerProps = this.props.handlerComponentProps || {};

            // If there is an error, render the specified error handler
            const handlerProps = {
                ...additionalHandlerProps,
                reloadComponent: () => {
                    // This will cause a re-render with no error UI.
                    this.setState({ error: undefined, errorInfo: undefined });
                },
                error: this.state.error,
                errorInfo: this.state.errorInfo
            };
            return React.createElement(
                WrappedComponent,
                {
                    wrappedType: this.props.handlerComponentType,
                    wrappedProps: handlerProps,
                    key: "error-boundary-ui"
                } as IWrappedComponentProperties,
                []
            );
        } else {
            // If no error, this component is a no-op
            return this.props.children;
        }
    }
}
